import { ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useConfirm } from 'primevue/useconfirm';

// ─────────────────────────────────────────────────────────
// Composable Imports
// ─────────────────────────────────────────────────────────

// Cell renderers
import { renderCell, formatActions } from './datatableCellRenderers';
// Export functionality
import useDatatableExport from './useDatatableExport';
// URL‐based preset filters
import useUrlPresetFilters from './useUrlPresetFilters';
// Column visibility and persistence
import useColumnVisibility from './useColumnVisibility';
// API request wrapper
import useTableApi from './useTableApi';
// Pagination (page, rows, sort)
import usePagination from './usePagination';
// Filtering logic
import useDatatableFiltering from './useDatatableFiltering';
// Row/context menu actions (confirm dialogs, actions)
import useDatatableActions from './useDatatableActions';
// Month selector (last six months)
import useMonthSelector from './useMonthSelector';

export function useDatatables(props, { emit }, options = {}) {
    // ─────────────────────────────────────────────────────────
    // Custom‐options destructuring
    // ─────────────────────────────────────────────────────────
    const {
        rowContextMenuRef: passedRowContextMenuRef,
        tableContextMenuRef: passedTableContextMenuRef,
        processResponse: customProcessResponse,
    } = options;

    // ─────────────────────────────────────────────────────────
    // Reactive State: Data & Summary
    // ─────────────────────────────────────────────────────────
    const data = ref([]);
    const summary = ref([]);
    const selectedData = ref([]);
    const selectionMode = ref('single');

    // ─────────────────────────────────────────────────────────
    // Reactive State: Column Configuration
    // ─────────────────────────────────────────────────────────
    const columns = ref([]);
    const sortableColumns = ref([]);

    // ─────────────────────────────────────────────────────────
    // Nested‐table specific state
    // ─────────────────────────────────────────────────────────
    const nestedColumns = ref([]);
    const toggleableNestedColumns = ref([]);
    const visibleNestedColumns = ref([]);
    const expandedRows = ref({});
    function isExpandable(rowData) {
        return rowData.some(row => row.children);
    }

    // ─────────────────────────────────────────────────────────
    // Column Visibility (toggle + localStorage)
    // ─────────────────────────────────────────────────────────
    const {
        visibleColumns,
        toggleableColumns,
        onToggleColumns,
        setColumnVisibilityFromCache,
    } = useColumnVisibility(props.resource, props.type, columns);

    // ─────────────────────────────────────────────────────────
    // Data Fetching & Response Processing
    // ─────────────────────────────────────────────────────────
    const { fetchData: _fetchData } = useTableApi(props, options);

    function fetchData() {
        loading.value = true;

        const params = {
            page: page.value,
            rows: rows.value,
            filters: filters.value,
            sortField: sortField.value,
            sortOrder: sortOrder.value,
            userId: props.userId,
            groupId: props.groupId,
            eId: props.eId,
            batchId: props.batchId,
            whitelistId: props.whitelistId,
            objectType: props.objectType,
            objectId: props.objectId,
            month: selectedMonth.value,
            filterPreset: filterPreset.value.type,
        };

        _fetchData(params)
            .then(response => {
                processResponse(response);
                setColumnVisibilityFromCache();
                loading.value = false;
            })
            .catch(err => {
                console.error('Error fetching data:', err);
                loading.value = false;
            });
    }

    const processResponse = customProcessResponse || function defaultProcessResponse(response) {
        data.value = response.data.data;
        columns.value = response.data.columns;
        sortableColumns.value = columns.value.filter(col => col.sortable);
        toggleableColumns.value = columns.value.filter(col => col.toggleable);
        globalFilterFields.value = columns.value
            .filter(col => col.global)
            .map(col => col.field);

        totalRecords.value = response.data.totalRecords;
        filteredRecords.value = response.data.filteredRecords;

        if (response.data.summary) {
            summary.value = response.data.summary;
        }

        if (!filtersInitialized.value) {
            initFilters();
            visibleColumns.value = columns.value;
            filtersInitialized.value = true;
        }
    };

    // ─────────────────────────────────────────────────────────
    // Preset Filters (based on URL query)
    // ─────────────────────────────────────────────────────────
    const { filterPreset, applyPresetFiltersFromURL, removePresetFilters } =
        useUrlPresetFilters(props.resource);

    // ─────────────────────────────────────────────────────────
    // Filtering Logic (simple vs. advanced)
    // ─────────────────────────────────────────────────────────
    const filterModes = ref([
        { label: 'Simple Filter', value: false },
        { label: 'Advanced Filter', value: true },
    ]);

    const {
        filters,
        advancedFilter,
        filtersInitialized,
        globalFilterFields,
        initFilters,
        isFilterActive,
        getFilterMatchModeOptions,
    } = useDatatableFiltering(columns, fetchData);

    function onFilter(event) {
        filters.value = event.filters;
        page.value = 0;
        fetchData();
    }

    function clearFilter() {
        initFilters();
        fetchData();
    }

    // ─────────────────────────────────────────────────────────
    // Pagination & Sorting (page, rows, sortField, sortOrder)
    // ─────────────────────────────────────────────────────────
    const { page, rows, sortField, sortOrder, onPageChange, onSortChange } =
        usePagination(fetchData, Number(props.rows));

    // ─────────────────────────────────────────────────────────
    // Month Selector (last six months)
    // ─────────────────────────────────────────────────────────
    const { selectedMonth, availableMonths } =
        useMonthSelector(props.monthSelect, fetchData);

    // ─────────────────────────────────────────────────────────
    // Loading & Record Counts
    // ─────────────────────────────────────────────────────────
    const loading = ref(false);
    const totalRecords = ref(0);
    const filteredRecords = ref(0);

    // ─────────────────────────────────────────────────────────
    // Row/Context Menus & Actions
    // ─────────────────────────────────────────────────────────
    const {
        rowContextMenuRef,
        tableContextMenuRef,
        rowMenuModel,
        tableMenuModel,
        onRowContextMenu,
        onTableContextMenu,
        handleAction,
        performAction,
        view,
        edit,
    } = useDatatableActions(props.resource, props.type);

    // ─────────────────────────────────────────────────────────
    // CSV/Excel Export
    // ─────────────────────────────────────────────────────────
    const { exportCSV } = useDatatableExport(
        props,
        page,
        rows,
        filters,
        sortField,
        sortOrder
    );

    // ─────────────────────────────────────────────────────────
    // Computed Properties
    // ─────────────────────────────────────────────────────────
    const filterDisplayMode = computed(() =>
        advancedFilter.value ? 'menu' : 'row'
    );

    // ─────────────────────────────────────────────────────────
    // Row Selection Toggle
    // ─────────────────────────────────────────────────────────
    function toggleSelectionMode() {
        selectedData.value = null;
        selectionMode.value =
            selectionMode.value === 'single' ? 'multiple' : 'single';
    }

    // ─────────────────────────────────────────────────────────
    // Utility: Debounce
    // ─────────────────────────────────────────────────────────
    function debounce(func, wait) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    const debouncedFetch = debounce(() => {
        fetchData();
    }, 500);

    // ─────────────────────────────────────────────────────────
    // Watchers
    // ─────────────────────────────────────────────────────────
    watch(() => props.resource, () => {
        fetchData();
    });
    watch(() => props.type, () => {
        fetchData();
    });
    watch(() => filters.value.global.value, () => {
        page.value = 0;
        debouncedFetch();
    });
    watch(selectedMonth, () => {
        fetchData({ month: selectedMonth.value });
    });
    watch(advancedFilter, () => {
        initFilters();
        fetchData();
    });

    // ─────────────────────────────────────────────────────────
    // Lifecycle Hook: onMounted
    // ─────────────────────────────────────────────────────────
    onMounted(() => {
        applyPresetFiltersFromURL();

        if (props.monthSelect) {
            // Build last 6 months for the selector
            const today = new Date();
            for (let i = 0; i < 6; i++) {
                const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
                const month = date.getMonth() + 1;
                const year = date.getFullYear();
                const monthString = month < 10 ? `0${month}` : month;
                availableMonths.value.push({
                    label: `${year}-${monthString}`,
                    value: `${year}-${monthString}`,
                });
            }
            selectedMonth.value = availableMonths.value[0].value;
        } else {
            fetchData();
        }
    });

    // ─────────────────────────────────────────────────────────
    // Return All Public Refs & Methods
    // ─────────────────────────────────────────────────────────
    return {
        // Data & Summary
        data,
        summary,
        selectedData,
        selectionMode,

        // Column configuration
        columns,
        sortableColumns,
        toggleableColumns,
        visibleColumns,
        onToggleColumns,

        // Nested‐table extras
        nestedColumns,
        toggleableNestedColumns,
        visibleNestedColumns,
        expandedRows,
        isExpandable,

        // Filtering
        filters,
        advancedFilter,
        filterModes,
        globalFilterFields,
        filtersInitialized,
        initFilters,
        onFilter,
        clearFilter,
        isFilterActive,
        getFilterMatchModeOptions,
        filterDisplayMode,

        // Pagination & Sorting
        rows,
        page,
        sortField,
        sortOrder,
        onPageChange,
        onSortChange,

        // Month selector
        selectedMonth,
        availableMonths,

        // Loading & counts
        loading,
        totalRecords,
        filteredRecords,

        // Preset filters
        filterPreset,
        removePresetFilters,

        // Menus & Actions
        rowContextMenuRef,
        rowMenuModel,
        tableContextMenuRef,
        tableMenuModel,
        onRowContextMenu,
        onTableContextMenu,
        handleAction,
        performAction,
        view,
        edit,

        // Export
        exportCSV,

        // Selection toggle
        toggleSelectionMode,

        // Cell rendering
        renderCell,
        formatActions,

        // Debounce utility (in case needed)
        debounce,

        // Custom response (for nested)
        processResponse,
        fetchData,

        // API helper
        FilterMatchMode,
    };
}
